import { Updating, UpdateError, UpdateSuccess, PartialSuccess } from './';
import { useEffect, useMemo } from 'react';
import { useBulkEdit } from '../../context';
import {
  Stack,
  DialogContent,
  Button,
  DialogActions,
} from '@mui/material';
import { ModalSheetDialog as Dialog } from '../styledComponents';
import { ApplyEditsDialogProps } from '../../constants/types';

export const ApplyEdits = ({
  ids = [],
  adGroups = [],
  fieldValues = {},
  open,
  onDone,
  onApplyEdits,
}: ApplyEditsDialogProps) => {
  const { updateError, updateResult, isUpdating } = useBulkEdit();

  useEffect(() => {
    if (open && ids.length > 0) {
      onApplyEdits({
        type: 'LINEITEM',
        select: {
          id__in: ids,
        },
        fields: fieldValues,
      }).catch(error => {
        console.error('Error applying edits:', error);
      });
    }
  }, [open, ids, fieldValues, onApplyEdits]);

  const adGroupCount = ids.length;
  const filteredAdGroups = adGroups.filter(adGroup =>
    ids.includes(adGroup.id)
  );

  const status = useMemo(() => {
    if (!updateResult || updateError) return 'ERROR';

    if (updateResult.results.every(result => result.status === 'SUCCESS')) {
      return 'SUCCESS';
    }

    if (updateResult.results.every(result => result.status === 'ERROR')) {
      return 'ERROR';
    }

    return 'PARTIAL_SUCCESS';
  }, [updateResult, updateError]);

  const renderContent = () => {
    if (isUpdating) return <Updating adGroupCount={adGroupCount} />;

    switch (status) {
      case 'ERROR':
        return <UpdateError />;
      case 'PARTIAL_SUCCESS':
        return (
          <PartialSuccess
            results={updateResult?.results || []}
            adGroups={filteredAdGroups}
          />
        );
      case 'SUCCESS':
        return <UpdateSuccess adGroupCount={adGroupCount} />;
      default:
        return <Updating adGroupCount={adGroupCount} />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      fullWidth
      maxWidth="lg"
      sx={{ pt: 2, pb: 2 }}
      data-testid="apply-edits-dialog"
    >
      <DialogContent sx={{ p: 3 }}>
        <Stack sx={{ minHeight: '20vh', justifyContent: 'center' }}>
          {renderContent()}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 4, py: 3 }}>
        {onDone && (
          <Button
            variant="contained"
            color="primary"
            onClick={onDone}
            // Instead of conditional rendering, hide button when updating to keep spacing consistent
            sx={{ visibility: isUpdating ? 'hidden' : 'visible' }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
